"use client";

import { useEffect, useMemo, useState } from "react";
import {
  inchaustiAudit,
  inchaustiFileUrl,
  type InchaustiFileCategory,
} from "../inchausti-data";

type Tab = "provenance" | "files" | "method";
type FileFilter = "all" | InchaustiFileCategory;

const copy = {
  zh: {
    provenance: "来源链",
    files: "Zenodo 文件",
    method: "方法核验",
    exact: "以下信息分别取自正式论文、SRA/ENA、Zenodo和作者GitHub；不同来源不一致之处会单独标明。",
    downloadAudit: "下载机器可读核验清单",
    rawTitle: "原始测序记录",
    processedTitle: "作者提交的处理文件",
    clustersTitle: "论文报告的细胞群",
    sourceSample: "来源样本",
    experiment: "实验",
    run: "运行",
    pairedFastq: "双端FASTQ",
    technicalMatrices: "技术矩阵",
    commonBarcodes: "共同条形码",
    postQc: "质量过滤后",
    singlets: "去除双细胞后",
    beforeDoublets: "论文报告；双细胞去除前",
    download: "下载",
    searchFiles: "搜索文件名、类别、说明或MD5",
    all: "全部",
    quantification: "定量矩阵",
    annotation: "参考注释",
    geneSet: "基因集",
    supporting: "支持数据",
    result: "个文件",
    matrixTitle: "两个技术重复矩阵如何合并",
    mergeNote: "公开R脚本取共同条形码、重新排序并将两个矩阵相加；两个基因列表逐字节完全一致。",
    paperWorkflow: "正式论文方法",
    codeWorkflow: "公开R脚本",
    status: "核验结论",
    discrepancyTitle: "论文阈值 ≠ 公开代码阈值",
    noReconstruction: "本站不重建UMAP",
    noReconstructionBody:
      "Zenodo记录没有最终细胞元数据、作者UMAP坐标、Seurat对象或H5AD文件。在作者确认过滤阈值并提供最终对象前，本站仅开放来源与矩阵下载，不生成可能偏离论文的图谱。",
    cluster2Hypothesis: "论文将cluster 2解释为推定过渡细胞；这是转录组推断，并非显微镜直接确认。",
    codeVersion: "核验的代码修订",
    checksum: "校验值",
    noFiles: "没有符合当前条件的文件。",
    noMarker: "论文图注未指定单一标志基因",
    experimentalSource: "实验来源",
    repositoryIsolation: "SRA来源字段",
    sourceBoundary:
      "SRA的“Axenic culture”是仓库字段，不替代论文中的感染H9c2成肌细胞实验来源。",
  },
  en: {
    provenance: "Provenance",
    files: "Zenodo files",
    method: "Method audit",
    exact:
      "Fields are kept separate by source: version-of-record paper, SRA/ENA, Zenodo and author GitHub. Disagreements are explicitly flagged.",
    downloadAudit: "Download machine-readable audit",
    rawTitle: "Raw sequencing record",
    processedTitle: "Author-deposited processed files",
    clustersTitle: "Cell populations reported in the paper",
    sourceSample: "source sample",
    experiment: "experiment",
    run: "run",
    pairedFastq: "paired FASTQ",
    technicalMatrices: "technical matrices",
    commonBarcodes: "common barcodes",
    postQc: "post-QC",
    singlets: "after doublet removal",
    beforeDoublets: "paper-reported; before doublet removal",
    download: "Download",
    searchFiles: "Search filename, category, description or MD5",
    all: "All",
    quantification: "Quantification",
    annotation: "Annotation",
    geneSet: "Gene sets",
    supporting: "Supporting",
    result: "files",
    matrixTitle: "How the two technical matrices are merged",
    mergeNote:
      "The public R script intersects common barcodes, reorders them and sums the two matrices; both feature lists are byte-identical.",
    paperWorkflow: "Version-of-record Methods",
    codeWorkflow: "Public R script",
    status: "Audit conclusion",
    discrepancyTitle: "Paper thresholds ≠ public-code thresholds",
    noReconstruction: "No reconstructed UMAP",
    noReconstructionBody:
      "The Zenodo record contains no final cell metadata, author UMAP coordinates, Seurat object or H5AD file. Until the authors confirm the filtering thresholds and provide the final object, TrypCell exposes provenance and matrix downloads without generating an atlas that may diverge from the paper.",
    cluster2Hypothesis:
      "The paper describes cluster 2 as a putative transition population based on transcriptomic evidence; it was not directly confirmed by microscopy.",
    codeVersion: "Audited code revision",
    checksum: "Checksum",
    noFiles: "No files match these filters.",
    noMarker: "No single marker stated in the figure legend",
    experimentalSource: "Experimental source",
    repositoryIsolation: "SRA isolation field",
    sourceBoundary:
      "The SRA value “Axenic culture” is a repository field and does not replace the paper's infected-H9c2 experimental source.",
  },
};

const categoryLabels = {
  zh: {
    all: "全部",
    quantification: "定量矩阵",
    annotation: "参考注释",
    "gene-set": "基因集",
    supporting: "支持数据",
  },
  en: {
    all: "All",
    quantification: "Quantification",
    annotation: "Annotation",
    "gene-set": "Gene sets",
    supporting: "Supporting",
  },
};

function formatBytes(bytes: number) {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} GiB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MiB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  return `${bytes} B`;
}

export function InchaustiAuditBrowser({
  auditDownloadUrl = "/api/tcruzi-inchausti-audit?download=1",
}: {
  auditDownloadUrl?: string;
} = {}) {
  const [language, setLanguage] = useState<"zh" | "en">("zh");
  const [tab, setTab] = useState<Tab>("provenance");
  const [fileFilter, setFileFilter] = useState<FileFilter>("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    const handleLanguage = (event: Event) => {
      setLanguage((event as CustomEvent<"en" | "zh">).detail);
    };
    window.addEventListener("trypcell-language", handleLanguage);
    setLanguage(document.body.dataset.language === "en" ? "en" : "zh");
    return () =>
      window.removeEventListener("trypcell-language", handleLanguage);
  }, []);

  const t = copy[language];
  const labels = categoryLabels[language];

  const visibleFiles = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return inchaustiAudit.zenodo.files.filter((file) => {
      const matchesCategory =
        fileFilter === "all" || file.category === fileFilter;
      const haystack = [
        file.name,
        file.category,
        file.md5,
        file.descriptionEn,
        file.descriptionZh,
      ]
        .join(" ")
        .toLowerCase();
      return matchesCategory && (!needle || haystack.includes(needle));
    });
  }, [fileFilter, query]);

  return (
    <div className="inchausti-browser">
      <section className="inchausti-source-note">
        <div>
          <span>FIVE-SOURCE AUDIT · 2026-07-26</span>
          <p>{t.exact}</p>
        </div>
        <a href={auditDownloadUrl} download>
          {t.downloadAudit} ↓
        </a>
      </section>

      <nav className="inchausti-tabs" aria-label="Dataset record sections">
        {[
          ["provenance", t.provenance],
          ["files", `${t.files} · 17`],
          ["method", t.method],
        ].map(([value, label]) => (
          <button
            type="button"
            key={value}
            className={tab === value ? "active" : ""}
            aria-pressed={tab === value}
            onClick={() => setTab(value as Tab)}
          >
            {label}
          </button>
        ))}
      </nav>

      {tab === "provenance" ? (
        <div className="inchausti-provenance">
          <section className="provenance-flow">
            {[
              ["01", "SAMN45910119", `1 ${t.sourceSample}`],
              ["02", "SRX27142470", `1 ${t.experiment}`],
              ["03", "SRR31781067", `1 ${t.run}`],
              ["04", "R1 + R2", `2 ${t.pairedFastq}`],
              ["05", "Matrix 1 + 2", `2 ${t.technicalMatrices}`],
              [
                "06",
                "82,751",
                t.commonBarcodes,
              ],
              ["07", "3,192", t.postQc],
              ["08", "3,151", t.singlets],
            ].map(([number, value, label]) => (
              <div key={number}>
                <span>{number}</span>
                <strong>{value}</strong>
                <small>{label}</small>
              </div>
            ))}
          </section>

          <section className="inchausti-record-grid">
            <article className="inchausti-record-card raw-record">
              <header>
                <div>
                  <span>SRA / ENA</span>
                  <h2>{t.rawTitle}</h2>
                </div>
                <a
                  href={inchaustiAudit.sources.enaRun}
                  target="_blank"
                  rel="noreferrer"
                >
                  SRR31781067 ↗
                </a>
              </header>
              <dl>
                {[
                  ["BioProject", "PRJNA1200704"],
                  ["BioSample", "SAMN45910119"],
                  ["Experiment", "SRX27142470"],
                  ["Organism / strain", "Trypanosoma cruzi · DM28c"],
                  [
                    t.experimentalSource,
                    "Infected H9c2 rat myoblasts · ATCC CRL-1446",
                  ],
                  [t.repositoryIsolation, "Axenic culture"],
                  ["Library", "10X Chromium · PolyA · PAIRED"],
                  ["Instrument", "Illumina HiSeq 2500"],
                  ["Reads", "882,316,647"],
                  ["Bases", "104,995,680,993"],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
              <p className="record-source-boundary">{t.sourceBoundary}</p>
              <div className="inchausti-fastq-list">
                {inchaustiAudit.rawSequencing.fastq.map((file) => (
                  <a
                    key={file.name}
                    href={file.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <div>
                      <strong>{file.name}</strong>
                      <small>
                        {formatBytes(file.bytes)} · MD5 {file.md5}
                      </small>
                    </div>
                    <b>{t.download} ↓</b>
                  </a>
                ))}
              </div>
            </article>

            <article className="inchausti-record-card processed-record">
              <header>
                <div>
                  <span>ZENODO 18760090 · CC BY 4.0</span>
                  <h2>{t.processedTitle}</h2>
                </div>
                <a
                  href={inchaustiAudit.sources.zenodo}
                  target="_blank"
                  rel="noreferrer"
                >
                  Zenodo ↗
                </a>
              </header>
              <div className="processed-summary">
                <div>
                  <strong>17</strong>
                  <span>{t.files}</span>
                </div>
                <div>
                  <strong>{formatBytes(inchaustiAudit.zenodo.totalBytes)}</strong>
                  <span>Total</span>
                </div>
                <div>
                  <strong>2</strong>
                  <span>Matrix Market</span>
                </div>
              </div>
              <div className="matrix-summary">
                <h3>{t.matrixTitle}</h3>
                {inchaustiAudit.quantification.matrices.map((matrix) => (
                  <div key={matrix.name}>
                    <code>{matrix.name}</code>
                    <span>
                      {matrix.barcodeRows.toLocaleString()} ×{" "}
                      {matrix.geneColumns.toLocaleString()}
                    </span>
                    <small>
                      {matrix.nonZeroEntries.toLocaleString()} non-zero entries
                    </small>
                  </div>
                ))}
                <p>
                  <strong>82,751</strong> {t.mergeNote}
                </p>
              </div>
            </article>
          </section>

          <section className="paper-clusters">
            <header>
              <div>
                <span>FIGURE 1 · VERSION OF RECORD</span>
                <h2>{t.clustersTitle}</h2>
              </div>
              <small>{t.beforeDoublets}</small>
            </header>
            <div>
              {inchaustiAudit.paper.reportedClusters.map((cluster) => (
                <article key={cluster.id}>
                  <span>Cluster {cluster.id}</span>
                  <strong>{cluster.cellsBeforeDoubletRemoval}</strong>
                  <p>
                    {language === "zh" ? cluster.labelZh : cluster.labelEn}
                  </p>
                  <code>{cluster.marker ?? t.noMarker}</code>
                </article>
              ))}
            </div>
            <p>{t.cluster2Hypothesis}</p>
          </section>
        </div>
      ) : null}

      {tab === "files" ? (
        <section className="inchausti-files">
          <div className="inchausti-file-tools">
            <label>
              <span aria-hidden="true">⌕</span>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t.searchFiles}
              />
            </label>
            <div aria-label="Filter Zenodo files">
              {(
                [
                  "all",
                  "quantification",
                  "annotation",
                  "gene-set",
                  "supporting",
                ] as FileFilter[]
              ).map((category) => (
                <button
                  type="button"
                  key={category}
                  className={fileFilter === category ? "active" : ""}
                  aria-pressed={fileFilter === category}
                  onClick={() => setFileFilter(category)}
                >
                  {labels[category]}
                </button>
              ))}
            </div>
          </div>
          <p className="inchausti-file-count">
            <strong>{visibleFiles.length}</strong> {t.result}
          </p>
          <div className="zenodo-file-list">
            {visibleFiles.map((file, index) => (
              <article key={file.name}>
                <span>{(index + 1).toString().padStart(2, "0")}</span>
                <div>
                  <small>{labels[file.category]}</small>
                  <code>{file.name}</code>
                  <p>
                    {language === "zh"
                      ? file.descriptionZh
                      : file.descriptionEn}
                  </p>
                </div>
                <div>
                  <strong>{formatBytes(file.bytes)}</strong>
                  <small>MD5 {file.md5}</small>
                </div>
                <a
                  href={inchaustiFileUrl(file)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t.download} ↓
                </a>
              </article>
            ))}
            {visibleFiles.length === 0 ? (
              <div className="inchausti-no-files">{t.noFiles}</div>
            ) : null}
          </div>
        </section>
      ) : null}

      {tab === "method" ? (
        <div className="inchausti-method">
          <section className="method-discrepancy">
            <header>
              <span>REPRODUCIBILITY FLAG</span>
              <h2>{t.discrepancyTitle}</h2>
            </header>
            <div className="threshold-table" role="table">
              <div role="row">
                <strong role="columnheader">Metric</strong>
                <strong role="columnheader">{t.paperWorkflow}</strong>
                <strong role="columnheader">{t.codeWorkflow}</strong>
              </div>
              {[
                [
                  "nUMI",
                  inchaustiAudit.methodAudit.paperThresholds.nUMI,
                  inchaustiAudit.methodAudit.publicCodeThresholds.nUMI,
                ],
                [
                  "nGene",
                  inchaustiAudit.methodAudit.paperThresholds.nGene,
                  inchaustiAudit.methodAudit.publicCodeThresholds.nGene,
                ],
                [
                  "log10GenesPerUMI",
                  inchaustiAudit.methodAudit.paperThresholds.log10GenesPerUMI,
                  inchaustiAudit.methodAudit.publicCodeThresholds
                    .log10GenesPerUMI,
                ],
                [
                  "mitoRatio",
                  inchaustiAudit.methodAudit.paperThresholds.mitoRatio,
                  inchaustiAudit.methodAudit.publicCodeThresholds.mitoRatio,
                ],
              ].map(([metric, paper, code]) => (
                <div role="row" key={metric}>
                  <code role="cell">{metric}</code>
                  <span role="cell">{paper}</span>
                  <span
                    role="cell"
                    className={paper !== code ? "different" : ""}
                  >
                    {code}
                  </span>
                </div>
              ))}
            </div>
            <div className="method-audit-conclusion">
              <span>{t.status}</span>
              <p>
                {language === "zh"
                  ? inchaustiAudit.methodAudit.statusZh
                  : inchaustiAudit.methodAudit.status}
              </p>
            </div>
          </section>

          <section className="method-facts">
            <article>
              <span>REFERENCE</span>
              <h3>Dm28c 2018 · TriTrypDB release 62</h3>
              <p>
                {language === "zh"
                  ? inchaustiAudit.quantification.referenceZh
                  : inchaustiAudit.quantification.reference}
              </p>
              <small>
                {language === "zh"
                  ? inchaustiAudit.quantification.utrAnnotationZh
                  : inchaustiAudit.quantification.utrAnnotation}
              </small>
            </article>
            <article>
              <span>QUANTIFICATION</span>
              <h3>kallisto bustools · --filter · --em</h3>
              <p>
                {language === "zh"
                  ? inchaustiAudit.quantification.noteZh
                  : inchaustiAudit.quantification.note}
              </p>
            </article>
            <article>
              <span>DOUBLETS</span>
              <h3>3,192 → 3,151 cells</h3>
              <p>
                {language === "zh"
                  ? inchaustiAudit.methodAudit.clusterCountBoundaryZh
                  : inchaustiAudit.methodAudit.clusterCountBoundary}
              </p>
            </article>
            <article>
              <span>{t.codeVersion}</span>
              <h3>{inchaustiAudit.codeAudit.repositoryRevision.slice(0, 12)}</h3>
              <p>
                {t.checksum}:{" "}
                <code>{inchaustiAudit.codeAudit.scriptSha256}</code>
              </p>
              <a
                href={inchaustiAudit.sources.code}
                target="_blank"
                rel="noreferrer"
              >
                Author GitHub ↗
              </a>
            </article>
          </section>

          <section className="no-reconstruction">
            <span>PORTAL BOUNDARY</span>
            <h2>{t.noReconstruction}</h2>
            <p>{t.noReconstructionBody}</p>
          </section>
        </div>
      ) : null}
    </div>
  );
}
